/**
 * TeamOnboardingModal
 *
 * A multi-step wizard for creating a single team with:
 * 1. Basic details (name, description)
 * 2. Permission configuration
 * 3. Initial member assignment
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import config from 'src/config';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import {
  UsersIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  InfoIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
} from '@phosphor-icons/react';
import useCreateGroups from 'src/api/organization/useCreateGroups';
import useOrganizationMembers from 'src/api/organization/useOrganizationMembers';
import { orpc } from '@/api/rpc/client';
import { Action, Subject } from '@erp/authz';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: (refetch?: boolean) => void;
}

type Step = 'details' | 'permissions' | 'members';

const MAX_NAME_LENGTH = 255;
const MAX_DESCRIPTION_LENGTH = 255;
const MIN_DESCRIPTION_LENGTH = 10;

interface PermissionState {
  [key: string]: boolean;
}

function permissionKey(action: Action, subject: Subject): string {
  return `${action}:${subject}`;
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  read: 'View',
  update: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  execute: 'Execute',
  report: 'Report',
};

function formatSubject(subject: string): string {
  return subject.replace(/([A-Z])/g, ' $1').trim();
}

export function TeamOnboardingModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();

  // Form state
  const [step, setStep] = useState<Step>('details');
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [permissionState, setPermissionState] = useState<PermissionState>({});
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [memberSearch, setMemberSearch] = useState('');

  // Created group ID (after step 1)
  const [createdGroupId, setCreatedGroupId] = useState<string | null>(null);

  // Fetch available permissions
  const { data: availableData, isLoading: availableLoading } = useQuery(
    orpc.authz.listAvailablePermissions.queryOptions(),
  );

  // Fetch organization members
  const { data: membersData, isLoading: membersLoading } =
    useOrganizationMembers();

  // Loading state for async operations
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  const [isAddingMembers, setIsAddingMembers] = useState(false);

  // Create group mutation - disable toast since we're in a wizard
  const createGroupMutation = useCreateGroups({
    showSuccessToast: false,
  });

  // Set permissions mutation
  const setPermissionsMutation = useMutation({
    mutationFn: async ({
      groupId,
      permissions,
    }: {
      groupId: string;
      permissions: { action: Action; subject: Subject }[];
    }) => {
      return orpc.authz.setGroupPermissions.call({
        groupId,
        permissions: permissions.map((p) => ({
          action: p.action,
          subject: p.subject,
          domain: null,
          subdomain: null,
          description: null,
        })),
      });
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: async ({
      groupId,
      userId,
    }: {
      groupId: string;
      userId: string;
    }) => {
      const response = await fetch(
        `${config.apiBaseUrl}/organization/groups/${groupId}/members`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ userId }),
        },
      );
      if (!response.ok) {
        throw new Error('Failed to add member');
      }
      return response.json();
    },
  });

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!membersData?.members) return [];
    const search = memberSearch.toLowerCase();
    return membersData.members.filter(
      (m) =>
        m.name.toLowerCase().includes(search) ||
        m.email.toLowerCase().includes(search),
    );
  }, [membersData?.members, memberSearch]);

  // Validation
  const nameLength = teamName.trim().length;
  const descriptionLength = teamDescription.trim().length;
  const isNameValid = nameLength > 0 && nameLength <= MAX_NAME_LENGTH;
  const isDescriptionValid =
    descriptionLength >= MIN_DESCRIPTION_LENGTH &&
    descriptionLength <= MAX_DESCRIPTION_LENGTH;
  const isDetailsValid = isNameValid && isDescriptionValid;

  // Get enabled permissions count
  const enabledPermissions = Object.entries(permissionState).filter(
    ([, enabled]) => enabled,
  );

  // Toggle a permission
  function handleTogglePermission(action: Action, subject: Subject) {
    const key = permissionKey(action, subject);
    setPermissionState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }

  // Toggle all permissions for a subject
  function handleToggleRow(subject: Subject, actions: Action[]) {
    const allEnabled = actions.every(
      (action) => permissionState[permissionKey(action, subject)],
    );
    const newState = { ...permissionState };
    for (const action of actions) {
      newState[permissionKey(action, subject)] = !allEnabled;
    }
    setPermissionState(newState);
  }

  // Toggle member selection
  function handleToggleMember(userId: string) {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  // Navigate between steps
  async function handleNext() {
    if (step === 'details') {
      setIsCreatingGroup(true);
      try {
        // Create the group and get the ID from response
        const response = await createGroupMutation.mutateAsync({
          groups: [
            {
              name: teamName.trim(),
              description: teamDescription.trim(),
            },
          ],
        });

        // Get the created group ID from the response
        const createdGroup = response.data?.groups?.[0];
        if (createdGroup) {
          setCreatedGroupId(createdGroup.id);
          setStep('permissions');
        }
      } catch (error) {
        // Error is handled by mutation, just log
        console.error('Failed to create group:', error);
      } finally {
        setIsCreatingGroup(false);
      }
    } else if (step === 'permissions') {
      // Save permissions if any selected and we have a group ID
      if (createdGroupId && enabledPermissions.length > 0) {
        setIsSavingPermissions(true);
        try {
          const permissions = enabledPermissions.map(([key]) => {
            const [action, subject] = key.split(':') as [Action, Subject];
            return { action, subject };
          });
          await setPermissionsMutation.mutateAsync({
            groupId: createdGroupId,
            permissions,
          });
        } catch (error) {
          console.error('Failed to save permissions:', error);
        } finally {
          setIsSavingPermissions(false);
        }
      }
      setStep('members');
    }
  }

  async function handleFinish() {
    setIsAddingMembers(true);
    try {
      // Add selected members
      if (createdGroupId && selectedMemberIds.size > 0) {
        const addMemberPromises = Array.from(selectedMemberIds).map((userId) =>
          addMemberMutation
            .mutateAsync({ groupId: createdGroupId, userId })
            .catch((error) => {
              console.error('Failed to add member:', userId, error);
            }),
        );
        await Promise.all(addMemberPromises);
      }

      // Invalidate queries and close
      queryClient.invalidateQueries({ queryKey: ['organization/groups'] });
      onClose(true);
    } finally {
      setIsAddingMembers(false);
    }
  }

  const isLoading = isCreatingGroup || isSavingPermissions || isAddingMembers;

  const displayActions =
    availableData?.actions.filter((a) => a !== Action.Manage) ?? [];

  // Helper to check if an action is valid for a subject
  function isActionValidForSubject(action: Action, subject: Subject): boolean {
    const validActions = availableData?.validActionsPerSubject[subject];
    return validActions?.includes(action) ?? false;
  }

  // Modal width varies by step - details is narrow, permissions needs to be wide
  const modalWidth =
    step === 'details' ? 'sm:max-w-xl w-[600px]' : 'sm:max-w-[90vw] w-[1400px]';

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent
        className={cn(
          modalWidth,
          'h-[85vh] flex flex-col p-0 gap-0 transition-all duration-300',
        )}
      >
        {/* Header */}
        <div className="shrink-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-b">
          <DialogHeader className="p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg">
                <UsersIcon className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl">Create Team</DialogTitle>
                <DialogDescription>
                  Set up a new team with permissions and members
                </DialogDescription>
              </div>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mt-4">
              <StepIndicator
                step={1}
                label="Details"
                isActive={step === 'details'}
                isComplete={step !== 'details'}
              />
              <div className="flex-1 h-px bg-border" />
              <StepIndicator
                step={2}
                label="Permissions"
                isActive={step === 'permissions'}
                isComplete={step === 'members'}
              />
              <div className="flex-1 h-px bg-border" />
              <StepIndicator
                step={3}
                label="Members"
                isActive={step === 'members'}
                isComplete={false}
              />
            </div>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {/* Step 1: Details */}
          {step === 'details' && (
            <div className="space-y-6 max-w-lg">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="team-name" className="text-sm font-medium">
                      Team Name <span className="text-destructive">*</span>
                    </Label>
                    <span
                      className={cn(
                        'text-xs',
                        nameLength > MAX_NAME_LENGTH
                          ? 'text-destructive'
                          : 'text-muted-foreground',
                      )}
                    >
                      {nameLength}/{MAX_NAME_LENGTH}
                    </span>
                  </div>
                  <Input
                    id="team-name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="e.g., Sales Team, Accounting, Warehouse Staff"
                    maxLength={MAX_NAME_LENGTH}
                    autoFocus
                    className={cn(
                      nameLength > 0 && !isNameValid && 'border-destructive',
                    )}
                  />
                  {nameLength > MAX_NAME_LENGTH && (
                    <p className="text-xs text-destructive">
                      Name must be {MAX_NAME_LENGTH} characters or less
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="team-description"
                      className="text-sm font-medium"
                    >
                      Description <span className="text-destructive">*</span>
                    </Label>
                    <span
                      className={cn(
                        'text-xs',
                        descriptionLength > MAX_DESCRIPTION_LENGTH ||
                          (descriptionLength > 0 &&
                            descriptionLength < MIN_DESCRIPTION_LENGTH)
                          ? 'text-destructive'
                          : 'text-muted-foreground',
                      )}
                    >
                      {descriptionLength}/{MAX_DESCRIPTION_LENGTH}
                    </span>
                  </div>
                  <Textarea
                    id="team-description"
                    value={teamDescription}
                    onChange={(e) => setTeamDescription(e.target.value)}
                    placeholder="Describe this team's responsibilities..."
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    rows={3}
                    className={cn(
                      descriptionLength > 0 &&
                        !isDescriptionValid &&
                        'border-destructive',
                    )}
                  />
                  {descriptionLength > 0 &&
                    descriptionLength < MIN_DESCRIPTION_LENGTH && (
                      <p className="text-xs text-destructive">
                        Description must be at least {MIN_DESCRIPTION_LENGTH}{' '}
                        characters
                      </p>
                    )}
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-3">
                <div className="flex gap-3">
                  <InfoIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Teams define what users can access and modify. You'll
                    configure permissions and add members in the next steps.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Permissions */}
          {step === 'permissions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Configure Permissions</h3>
                  <p className="text-sm text-muted-foreground">
                    Select what this team can access and modify
                  </p>
                </div>
                <Badge variant="secondary">
                  {enabledPermissions.length} selected
                </Badge>
              </div>

              {availableLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : (
                <div className="space-y-4">
                  {availableData?.domainGroups.map((domain) => (
                    <div
                      key={domain.domain}
                      className="rounded-lg border bg-card overflow-hidden"
                    >
                      <div className="bg-muted/50 px-3 py-2 font-medium text-sm border-b">
                        {domain.domain}
                      </div>
                      <div className="p-3 space-y-4">
                        {domain.subdomains.map((subdomain) => (
                          <div key={subdomain.subdomain}>
                            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                              {subdomain.subdomain}
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-2 pr-4 font-medium text-muted-foreground w-[180px]">
                                      Resource
                                    </th>
                                    {displayActions.map((action) => (
                                      <th
                                        key={action}
                                        className="px-2 py-2 text-center font-medium text-muted-foreground w-[70px]"
                                      >
                                        {ACTION_LABELS[action] ?? action}
                                      </th>
                                    ))}
                                    <th className="w-[50px]" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {subdomain.subjects.map((subject, idx) => {
                                    // Get valid actions for this subject
                                    const validActionsForSubject =
                                      displayActions.filter((action) =>
                                        isActionValidForSubject(
                                          action,
                                          subject,
                                        ),
                                      );
                                    const allEnabled =
                                      validActionsForSubject.length > 0 &&
                                      validActionsForSubject.every(
                                        (a) =>
                                          permissionState[
                                            permissionKey(a, subject)
                                          ],
                                      );

                                    return (
                                      <tr
                                        key={subject}
                                        className={cn(
                                          'group hover:bg-muted/30',
                                          idx !==
                                            subdomain.subjects.length - 1 &&
                                            'border-b',
                                        )}
                                      >
                                        <td className="py-2 pr-4 font-medium">
                                          {formatSubject(subject)}
                                        </td>
                                        {displayActions.map((action) => {
                                          const isValidAction =
                                            isActionValidForSubject(
                                              action,
                                              subject,
                                            );
                                          const key = permissionKey(
                                            action,
                                            subject,
                                          );
                                          const isChecked =
                                            permissionState[key] || false;

                                          // Show empty cell for invalid action/subject combinations
                                          if (!isValidAction) {
                                            return (
                                              <td
                                                key={action}
                                                className="px-2 py-2 text-center"
                                              >
                                                <span className="text-muted-foreground/30">
                                                  —
                                                </span>
                                              </td>
                                            );
                                          }

                                          return (
                                            <td
                                              key={action}
                                              className="px-2 py-2 text-center"
                                            >
                                              <div className="flex justify-center">
                                                <Checkbox
                                                  checked={isChecked}
                                                  onCheckedChange={() =>
                                                    handleTogglePermission(
                                                      action,
                                                      subject,
                                                    )
                                                  }
                                                />
                                              </div>
                                            </td>
                                          );
                                        })}
                                        <td className="px-2 py-2">
                                          {validActionsForSubject.length >
                                            0 && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-6 px-2 text-xs opacity-0 group-hover:opacity-100"
                                              onClick={() =>
                                                handleToggleRow(
                                                  subject,
                                                  validActionsForSubject,
                                                )
                                              }
                                            >
                                              {allEnabled ? 'None' : 'All'}
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Members */}
          {step === 'members' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Add Members</h3>
                  <p className="text-sm text-muted-foreground">
                    Select users to add to this team
                  </p>
                </div>
                <Badge variant="secondary">
                  {selectedMemberIds.size} selected
                </Badge>
              </div>

              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {membersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserPlusIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No members found</p>
                </div>
              ) : (
                <div className="space-y-1 max-h-[400px] overflow-y-auto rounded-lg border bg-card p-1">
                  {filteredMembers.map((member) => {
                    const isSelected = selectedMemberIds.has(member.userId);
                    return (
                      <div
                        key={member.userId}
                        className={cn(
                          'flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors',
                          isSelected ? 'bg-primary/10' : 'hover:bg-muted/50',
                        )}
                        onClick={() => handleToggleMember(member.userId)}
                      >
                        <Checkbox checked={isSelected} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {member.name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.email}
                          </p>
                        </div>
                        {member.groups.length > 0 && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            {member.groups.length} team
                            {member.groups.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="rounded-lg border border-muted bg-muted/30 p-3">
                <p className="text-sm text-muted-foreground">
                  You can skip this step and add members later from the team
                  settings.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="shrink-0 border-t bg-muted/30 p-4 gap-2 sm:gap-0">
          {/* Step 1: Show Cancel button */}
          {step === 'details' && (
            <Button
              variant="outline"
              onClick={() => onClose()}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}

          {/* Steps 2-3: Show "Do Later" button - team is already created */}
          {step !== 'details' && (
            <Button
              variant="ghost"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: ['organization/groups'],
                });
                onClose(true);
              }}
              disabled={isLoading}
              className="mr-auto"
            >
              Do Later
            </Button>
          )}

          {step === 'details' && (
            <Button
              onClick={handleNext}
              disabled={!isDetailsValid || isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Team'}
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </Button>
          )}

          {step === 'permissions' && (
            <Button onClick={handleNext} disabled={isLoading}>
              {isLoading
                ? 'Saving...'
                : enabledPermissions.length > 0
                  ? 'Save & Continue'
                  : 'Skip Permissions'}
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </Button>
          )}

          {step === 'members' && (
            <Button onClick={handleFinish} disabled={isLoading}>
              {isLoading
                ? 'Adding...'
                : selectedMemberIds.size > 0
                  ? 'Add Members & Finish'
                  : 'Finish'}
              <CheckCircleIcon className="h-4 w-4 ml-2" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface StepIndicatorProps {
  step: number;
  label: string;
  isActive: boolean;
  isComplete: boolean;
}

function StepIndicator({
  step,
  label,
  isActive,
  isComplete,
}: StepIndicatorProps) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium transition-colors',
          isComplete
            ? 'bg-primary text-primary-foreground'
            : isActive
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground',
        )}
      >
        {isComplete ? <CheckCircleIcon className="h-4 w-4" /> : step}
      </div>
      <span
        className={cn(
          'text-sm font-medium',
          isActive ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
    </div>
  );
}

export default TeamOnboardingModal;
